import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Phone, Mail, MapPin, Calendar, Shield, CheckCircle, XCircle, Clock } from 'lucide-react';


const PlayerDetailsTab = ({ players }) => {
  const [expandedPlayers, setExpandedPlayers] = useState({});

  const togglePlayer = (playerId) => {
    setExpandedPlayers(prev => ({
      ...prev,
      [playerId]: !prev?.[playerId]
    }));
  };

  const getValidationIcon = (status) => {
    switch (status) {
      case 'verified':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getValidationStatusColor = (status) => {
    switch (status) {
      case 'verified':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'Captain':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Vice Captain':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const calculateAge = (dateOfBirth) => {
    const dob = new Date(dateOfBirth);
    const today = new Date();
    let age = today?.getFullYear() - dob?.getFullYear();
    const monthDiff = today?.getMonth() - dob?.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < dob?.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-gray-900">
          Player Roster ({players?.length || 0} Players)
        </h3>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="text-gray-600">Verified: {players?.filter(p => p?.validationStatus === 'verified')?.length || 0}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-yellow-600" />
            <span className="text-gray-600">Pending: {players?.filter(p => p?.validationStatus === 'pending')?.length || 0}</span>
          </div>
        </div>
      </div>
      <div className="space-y-3">
        {players?.map((player) => (
          <div
            key={player?.id}
            className="bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
          >
            {/* Player Header */}
            <div
              className="p-4 cursor-pointer"
              onClick={() => togglePlayer(player?.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {player?.name?.charAt(0)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-lg font-semibold text-gray-900 truncate">
                        {player?.name}
                      </h4>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeColor(player?.role)}`}>
                        {player?.role}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Shield className="w-4 h-4" />
                        {player?.mcaId}
                      </span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Age {calculateAge(player?.dateOfBirth)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    {getValidationIcon(player?.validationStatus)}
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getValidationStatusColor(player?.validationStatus)}`}>
                      {player?.validationStatus}
                    </span>
                  </div>
                  <div className="text-gray-400">
                    {expandedPlayers?.[player?.id] ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Expanded Player Details */}
            {expandedPlayers?.[player?.id] && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <Calendar className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Date of Birth</p>
                      <p className="text-base font-medium text-gray-900">
                        {new Date(player?.dateOfBirth)?.toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <Phone className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Phone Number</p>
                      <p className="text-base font-medium text-gray-900">{player?.phone}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <Mail className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email Address</p>
                      <p className="text-base font-medium text-gray-900 break-all">{player?.email}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <MapPin className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Address</p>
                      <p className="text-base font-medium text-gray-900">{player?.address}</p>
                    </div>
                  </div>
                </div>

                {/* Document Status */}
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      Document Status:
                    </span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      player?.documentsComplete
                        ? 'bg-green-100 text-green-800' :'bg-yellow-100 text-yellow-800'
                    }`}>
                      {player?.documentsComplete ? 'Complete' : 'Incomplete'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlayerDetailsTab;